import { trackClickEvent } from "@foundation/nextjs-gtm";
import WatchlistClient, {
  type GetUserWatchlistsResponse,
  type GetWatchlistCompareParams,
  type GetWatchlistLotsParams,
  type GetWatchlistLotsResponse,
  type GetWatchlistResponse,
  type UpdateWatchlistCompareParams,
  type UserPreferencesResponse,
  type WatchlistCompareResponse,
  type WatchlistResponse,
} from "@foundation/watchlist-client";
import { watchlistClient as octagonClient } from "~config/octagon-client";
import type { SubscriberType, UserPreference } from "~types/octagon";
import {
  CreatePortfolio,
  PortfolioResponse,
  UpdatePortfolio,
} from "~types/portfolio";
import { logError } from "~utils/newrelic/newrelicBrowser";

const WATCHLIST_SERVICE_URL = `${
  process.env.NEXT_PUBLIC_REGISTRATION_URL || "https://login.bloomberg.com"
}/watchlist-service`;
const watchlistClient = new WatchlistClient({
  watchlistServiceUrl: WATCHLIST_SERVICE_URL,
});

const wc = octagonClient.getInstance();

export function fetchWatchlistCompare({
  bconnectId,
  watchlistId,
}: GetWatchlistCompareParams): Promise<
  WatchlistResponse<WatchlistCompareResponse>
> {
  return watchlistClient
    .getWatchlistCompare({
      bconnectId,
      watchlistId,
    })
    .then((response) => {
      return response;
    })
    .catch((err) => {
      const formattedErrorMessage = `Failed to fetch watchlist compare: ${JSON.stringify(
        err
      )}`;

      logError(new Error(formattedErrorMessage));
      throw err;
    });
}

export function updateWatchlistCompare({
  bconnectId,
  watchlistId,
  watchlistCompareData,
}: UpdateWatchlistCompareParams): Promise<
  WatchlistResponse<WatchlistCompareResponse>
> {
  return watchlistClient
    .updateWatchlistCompare({ bconnectId, watchlistId, watchlistCompareData })
    .then((response) => {
      return response;
    })
    .catch((err) => {
      const formattedErrorMessage = `Failed to update watchlist compare: ${JSON.stringify(
        err
      )}`;

      logError(new Error(formattedErrorMessage));
      throw err;
    });
}

export function fetchWatchlistLots({
  bconnectId,
  watchlistId,
}: GetWatchlistLotsParams): Promise<
  WatchlistResponse<GetWatchlistLotsResponse>
> {
  return watchlistClient
    .getWatchlistLots({
      bconnectId,
      watchlistId,
    })
    .then((response) => {
      return response;
    })
    .catch((err) => {
      const formattedErrorMessage = `Failed to fetch watchlist lots: ${JSON.stringify(
        err
      )}`;

      logError(new Error(formattedErrorMessage));

      throw err;
    });
}

export async function fetchUserPreferences({
  bconnectId,
  subscription,
}: {
  bconnectId: string;
  subscription?: SubscriberType | null;
}): Promise<WatchlistResponse<UserPreference>> {
  try {
    const headers: Record<string, string> = {};

    if (subscription !== undefined) {
      const isSubscriber = subscription === "BBG";

      const metadata = Buffer.from(
        JSON.stringify({ isSubscriber }),
        "utf-8"
      ).toString("base64");

      headers["X-Request-Metadata"] = metadata;
    }

    const data = await wc.getUserPreference(bconnectId, {
      headers,
    });

    return {
      data,
      status: 200,
      message: "Success",
    };
  } catch (err) {
    trackClickEvent(
      "watchlistError",
      "Get User Preferences Failure",
      "error",
      "watchlist"
    );

    const formattedErrorMessage = `Failed to fetch user preferences: ${JSON.stringify(
      err
    )}`;

    window?.newrelic?.noticeError(new Error(formattedErrorMessage));

    throw err;
  }
}

export function updateUserPreferences({
  bconnectId,
  alertsNotificationsEnabled,
  alertsEmailsEnabled,
  dashboardView,
}: {
  bconnectId: string;
  alertsNotificationsEnabled?: boolean;
  alertsEmailsEnabled?: boolean;
  dashboardView?: string;
}): Promise<WatchlistResponse<UserPreferencesResponse>> {
  return watchlistClient
    .updateUserPreferences({
      bconnectId,
      alertsNotificationsEnabled,
      alertsEmailsEnabled,
      dashboardView,
    })
    .then((response) => {
      return response;
    })
    .catch((err) => {
      trackClickEvent(
        "watchlistError",
        "Update User Preferences Failure",
        "error",
        "watchlist"
      );
      const formattedErrorMessage = `Failed to update user preferences: ${JSON.stringify(
        err
      )}`;

      window?.newrelic?.noticeError(new Error(formattedErrorMessage));

      throw err;
    });
}

export function fetchPortfolioData({
  bconnectId,
  watchlistId,
}: {
  bconnectId: string;
  watchlistId?: number;
}): Promise<WatchlistResponse<GetUserWatchlistsResponse>> {
  return watchlistClient
    .getUserWatchlists({ bconnectId, watchlistId })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      trackClickEvent(
        "watchlistError",
        "Get Default Watchlist Failure",
        "error",
        "watchlist"
      );

      const formattedErrorMessage = `Failed to fetch Default Watchlist data: ${JSON.stringify(
        error
      )}`;

      window?.newrelic?.noticeError(new Error(formattedErrorMessage));

      throw error;
    });
}

export function fetchPortfolioById(
  bconnectId: string,
  watchlistId: number
): Promise<WatchlistResponse<GetWatchlistResponse>> {
  return watchlistClient
    .getWatchlist({ bconnectId, watchlistId })
    .then((response) => {
      return response;
    })
    .catch((error) => {
      trackClickEvent(
        "watchlistError",
        "Get Watchlist Failure",
        "error",
        "watchlist"
      );

      const formattedErrorMessage = `Failed to fetch Watchlist data: ${JSON.stringify(
        error
      )}`;

      window?.newrelic?.noticeError(new Error(formattedErrorMessage));

      throw error;
    });
}

export function createPortfolio(
  bconnectId: string,
  watchlistData: CreatePortfolio
): Promise<WatchlistResponse<GetWatchlistResponse>> {
  return watchlistClient
    .createWatchlist({ bconnectId, watchlistData })
    .then((response) => {
      trackClickEvent(
        "watchlistSubmitChanges",
        "submit_changes",
        "click",
        "engagement"
      );

      return response;
    })
    .catch((error) => {
      trackClickEvent(
        "watchlistError",
        "Create Watchlist Failure",
        "error",
        "watchlist"
      );

      const formattedErrorMessage = `Failed to Create Watchlist: ${JSON.stringify(
        error
      )}`;

      window?.newrelic?.noticeError(new Error(formattedErrorMessage));

      throw error;
    });
}

export function updatePortfolioById(
  bconnectId: string,
  watchlistId: number,
  watchlistData: UpdatePortfolio
): Promise<WatchlistResponse<GetWatchlistResponse>> {
  return watchlistClient
    .updateWatchlist({ bconnectId, watchlistId, watchlistData })
    .then((response) => {
      trackClickEvent(
        "watchlistSubmitChanges",
        "submit_changes",
        "success",
        "watchlist"
      );

      return response;
    })
    .catch((error) => {
      trackClickEvent(
        "watchlistError",
        "Update Watchlist Failure",
        "error",
        "watchlist"
      );

      const formattedErrorMessage = `Failed to Update Watchlist: ${JSON.stringify(
        error
      )}`;

      window?.newrelic?.noticeError(new Error(formattedErrorMessage));

      throw error;
    });
}

export function deletePortfolioById(
  bconnectId: string,
  watchlistId: number
): Promise<PortfolioResponse<string>> {
  return watchlistClient
    .deleteWatchlist({ bconnectId, watchlistId })
    .then((response) => {
      trackClickEvent("watchlistDelete", "delete", "success", "watchlist");

      return response;
    })
    .catch((error) => {
      trackClickEvent(
        "watchlistError",
        "Delete Watchlist Failure",
        "error",
        "watchlist"
      );

      const formattedErrorMessage = `Failed to Delete Watchlist: ${JSON.stringify(
        error
      )}`;

      window?.newrelic?.noticeError(new Error(formattedErrorMessage));

      throw error;
    });
}
